import type { Household, LeaderboardEntry } from './Household';
import { AVATARS } from './License';
import type { License } from './License';
import { COLLARS, unlockedCollar, nextCollar } from './Collars';

/** The care panel: care card, your name, care code / link, leaderboard. */
export class CarePanel {
  private el: HTMLElement;
  license: License | null = null;
  /** the residence hall's name changed (care pane) */
  onDorm?: (name: string) => void;

  constructor(
    private hh: Household,
    private dogName: string,
  ) {
    this.el = document.querySelector<HTMLElement>('#care')!;
    this.el.querySelector<HTMLButtonElement>('.care-close')!.addEventListener('click', () => this.close());
    const nameInput = this.el.querySelector<HTMLInputElement>('#careName')!;
    nameInput.addEventListener('change', () => void this.hh.setName(nameInput.value).then(() => this.render()));
    const dorm = this.el.querySelector<HTMLInputElement>('#careDorm')!;
    try {
      dorm.value = localStorage.getItem('lunaloo.dorm') ?? '';
    } catch {
      /* no storage */
    }
    dorm.addEventListener('change', () => {
      const name = dorm.value.trim();
      try {
        if (name) localStorage.setItem('lunaloo.dorm', name);
        else localStorage.removeItem('lunaloo.dorm');
      } catch {
        /* no storage */
      }
      this.onDorm?.(name);
    });
    // you: name + avatar, saved to the license (same call as joining; it upserts)
    const meName = this.el.querySelector<HTMLInputElement>('#careMeName')!;
    const meAvatar = this.el.querySelector<HTMLButtonElement>('#careMeAvatar')!;
    const grid = this.el.querySelector<HTMLElement>('#careAvatars')!;
    const meMsg = this.el.querySelector<HTMLElement>('#careMeMsg')!;
    const saveMe = async (): Promise<void> => {
      const lic = this.license;
      if (!lic) return;
      const name = meName.value.trim() || lic.me?.name || 'someone';
      const avatar = meAvatar.textContent || lic.me?.avatar || AVATARS[0];
      meMsg.textContent = 'saving…';
      const ok = await lic.join(name, avatar);
      meMsg.textContent = ok ? 'saved — everyone on the license sees it' : "couldn't reach the server";
      void this.renderMembers();
      this.onMeChange?.();
    };
    for (const a of AVATARS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = a;
      b.addEventListener('click', () => {
        meAvatar.textContent = a;
        grid.querySelectorAll('button').forEach((x) => x.classList.toggle('selected', x === b));
        grid.hidden = true;
        void saveMe();
      });
      grid.append(b);
    }
    meAvatar.addEventListener('click', () => {
      grid.hidden = !grid.hidden;
      grid.querySelectorAll('button').forEach((x) => x.classList.toggle('selected', x.textContent === meAvatar.textContent));
    });
    meName.addEventListener('change', () => void saveMe());
    this.el.querySelectorAll<HTMLButtonElement>('.care-nav button').forEach((b) => b.addEventListener('click', () => this.setPane(b.dataset.pane as 'care' | 'license')));
    this.el.querySelectorAll<HTMLButtonElement>('#careTabs button').forEach((b) =>
      b.addEventListener('click', () => {
        this.board = b.dataset.board as keyof LeaderboardEntry;
        this.el.querySelectorAll('#careTabs button').forEach((x) => x.classList.toggle('on', x === b));
        this.renderBoard();
      }),
    );
    const linkForm = this.el.querySelector<HTMLFormElement>('#careLink')!;
    linkForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = linkForm.querySelector<HTMLInputElement>('input')!;
      void this.hh.link(input.value).then((ok) => {
        this.el.querySelector<HTMLElement>('#careLinkMsg')!.textContent = ok ? 'linked — this phone now shares that household' : "couldn't find that code";
        input.value = '';
        this.render();
      });
    });
  }

  get isOpen(): boolean {
    return !this.el.hidden;
  }

  /** called every time the panel opens (the app fills in live status lines) */
  onOpen?: () => void;
  /** name / avatar changed (the holder chip may need redrawing) */
  onMeChange?: () => void;
  /** this device left (or deleted) its license: the app should forget it and start over */
  onLeftLicense?: () => void;

  private pane: 'care' | 'license' = 'care';
  setPane(pane: 'care' | 'license'): void {
    this.pane = pane;
    this.el.querySelectorAll<HTMLElement>('.care-pane').forEach((p) => (p.hidden = p.dataset.pane !== pane));
    this.el.querySelectorAll<HTMLButtonElement>('.care-nav button').forEach((b) => b.classList.toggle('on', b.dataset.pane === pane));
    const me = this.license?.me;
    this.el.querySelector<HTMLElement>('.care-title')!.textContent =
      pane === 'license' && me ? `${me.avatar} ${me.name} · ${this.dogName}` : `Caring for ${this.dogName}`;
    this.el.scrollTop = 0;
  }

  async open(pane: 'care' | 'license' = this.pane): Promise<void> {
    this.setPane(pane);
    this.onOpen?.();
    this.el.hidden = false;
    await this.hh.ensureCode();
    this.render();
    void this.renderHistory();
    void this.renderMembers();
    this.entries = await this.hh.leaderboard();
    this.renderBoard();
  }

  private entries: LeaderboardEntry[] = [];
  private board: keyof LeaderboardEntry = 'bestStreak';

  /** the leaderboard: every named household, ranked by the tab you're on */
  private renderBoard(): void {
    const list = this.el.querySelector<HTMLElement>('#careBoard')!;
    list.textContent = '';
    if (!this.entries.length) {
      list.textContent = 'No one on the board yet — give your household a name above.';
      return;
    }
    const k = this.board;
    const unit: Partial<Record<keyof LeaderboardEntry, (e: LeaderboardEntry) => string>> = {
      bestStreak: (e) => `${e.bestStreak}d streak · ${e.days} days`,
      earned: (e) => `${e.earned} 🪙 earned`,
      holes: (e) => `${e.holes} in the hole · ${e.cans} cans`,
      bestFetch: (e) => `${e.bestFetch.toFixed(1)} m · ${e.fetches} fetches`,
      squirrelWins: (e) => `${e.squirrelWins} caught up`,
      routines: (e) => `${e.routines} routines`,
      catches: (e) => `${e.catches} in-air catches`,
      agilityBest: (e) => `${e.agilityBest} pts · ${e.agilityRuns} runs`,
    };
    const sorted = [...this.entries].sort((a, b) => (b[k] as number) - (a[k] as number) || b.days - a.days || a.name.localeCompare(b.name));
    if (!this.hh.name) {
      const note = document.createElement('div');
      note.className = 'care-row';
      note.textContent = 'Give your household a name above to be on the board.';
      list.append(note);
    }
    sorted.slice(0, 25).forEach((e, i) => {
      const row = document.createElement('div');
      row.className = 'care-row' + (e.name === this.hh.name ? ' me' : '');
      row.innerHTML = `<span>${i + 1}.</span><span class="care-row-name"></span><span></span>`;
      row.querySelector('.care-row-name')!.textContent = `${e.name}${e.dogs.length > 1 ? ' 🐕🐕' : ''}`;
      row.lastElementChild!.textContent = unit[k]?.(e) ?? String(e[k]);
      list.append(row);
    });
  }

  private async renderMembers(): Promise<void> {
    const box = this.el.querySelector<HTMLElement>('#careMembers')!;
    if (!this.license) return;
    const members = await this.license.members();
    box.textContent = '';
    if (!members.length) {
      box.textContent = 'just you so far — share the code below';
      return;
    }
    const meDevice = this.license.device;
    const iOwn = members.find((m) => m.device === meDevice)?.role === 'owner';
    for (const m of members) {
      const row = document.createElement('div');
      row.className = 'care-row' + (m.device === meDevice ? ' me' : '');
      row.innerHTML = `<span>${m.avatar}</span><span class="care-row-name"></span><span>${m.role === 'owner' ? 'license holder' : 'carer'}</span><span class="care-row-x"></span>`;
      row.querySelector('.care-row-name')!.textContent = m.name + (m.device === meDevice ? ' (you)' : '');
      const actions = row.querySelector('.care-row-x')!;
      if (iOwn && m.role !== 'owner') {
        const give = document.createElement('button');
        give.type = 'button';
        give.textContent = 'make owner';
        give.addEventListener('click', () => {
          if (!confirm(`Hand ${this.dogName}'s license to ${m.name}? You stay on as a carer.`)) return;
          void this.license!.makeOwner(m.device).then(() => this.renderMembers());
        });
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = 'remove';
        b.addEventListener('click', () => {
          if (!confirm(`Remove ${m.name} from ${this.dogName}'s license?`)) return;
          void this.license!.remove(m.device).then(() => this.renderMembers());
        });
        actions.append(give, b);
      }
      box.append(row);
    }
    // leaving: a carer walks away; the owner (last one standing or not) can delete her for good
    const foot = document.createElement('div');
    foot.className = 'care-leave';
    if (!iOwn) {
      const leave = document.createElement('button');
      leave.type = 'button';
      leave.className = 'care-btn care-btn-quiet';
      leave.textContent = `leave ${this.dogName}'s license`;
      leave.addEventListener('click', () => {
        if (!confirm(`Leave ${this.dogName}'s license? The others keep her; this phone starts over.`)) return;
        void this.license!.remove(meDevice).then(() => this.onLeftLicense?.());
      });
      foot.append(leave);
    } else {
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'care-btn care-btn-danger';
      del.textContent = 'deactivate license';
      del.addEventListener('click', () => {
        if (!confirm(`Deactivate ${this.dogName}'s license? Every carer loses her, along with her days, tricks and credits. This can't be undone.`)) return;
        if (!confirm('Really deactivate it?')) return;
        void this.license!.deleteHousehold().then((ok) => {
          if (ok) this.onLeftLicense?.();
          else alert("couldn't reach the server — try again");
        });
      });
      foot.append(del);
    }
    box.append(foot);
  }

  private async renderHistory(): Promise<void> {
    const box = this.el.querySelector<HTMLElement>('#careHistory')!;
    box.textContent = 'loading…';
    const versions = await this.hh.history();
    box.textContent = '';
    if (!versions.length) {
      box.textContent = 'nothing saved yet';
      return;
    }
    // one row per distinct hour keeps the list short
    const seen = new Set<string>();
    for (const v of versions) {
      const d = new Date(v.at * 1000);
      const key = d.toISOString().slice(0, 13);
      if (seen.has(key) && !v.note) continue;
      seen.add(key);
      const row = document.createElement('div');
      row.className = 'care-row';
      const when = d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      row.innerHTML = `<span>⏱</span><span class="care-row-name"></span><span>${v.days}d · bowl ${Math.round(v.bowl * 100)}%</span><button type="button">restore</button>`;
      row.querySelector('.care-row-name')!.textContent = when + (v.note ? ` (${v.note})` : '');
      row.querySelector('button')!.addEventListener('click', () => {
        if (!confirm(`Go back to ${when}? Everything since then is undone (this itself can be undone).`)) return;
        void this.hh.restore(v.id).then((ok) => {
          if (ok) location.reload();
          else box.textContent = "couldn't restore — offline?";
        });
      });
      box.append(row);
    }
  }

  close(): void {
    this.el.hidden = true;
  }

  render(): void {
    const c = this.hh.care;
    const q = (sel: string) => this.el.querySelector<HTMLElement>(sel)!;
    this.setPane(this.pane);
    const me = this.license?.me;
    q('#careMeAvatar').textContent = me?.avatar ?? '🐾';
    const meInput = this.el.querySelector<HTMLInputElement>('#careMeName')!;
    if (document.activeElement !== meInput) meInput.value = me?.name ?? '';
    q('#careStreak').textContent = String(c.currentStreak);
    q('#careBest').textContent = String(c.state.bestStreak);
    q('#careDays').textContent = String(c.totalDays);
    const since = c.state.togetherSince ?? c.state.days[0];
    q('#careSince').textContent = since ? `together since ${new Date(since + 'T00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}` : '';
    q('#careQuality').textContent = c.moodFace;
    q('#careCredits').textContent = String(c.credits);
    q('#careQualityWord').textContent = c.qualityWord;
    const qs = c.qualitySeconds;
    q('#careTime').textContent = qs >= 3600 ? `${Math.floor(qs / 3600)}h ${Math.floor((qs % 3600) / 60)}m` : `${Math.floor(qs / 60)}m`;
    q('#careCounts').textContent = `${c.state.feeds} meals · ${c.state.fetches} fetches · ${c.state.tricksTaught} tricks · bowl ran empty ${c.state.emptyBowl}×`;
    q('#careCode').textContent = this.hh.code ?? '(needs internet once)';
    const share = this.el.querySelector<HTMLButtonElement>('#careShare')!;
    share.hidden = !this.hh.code;
    share.onclick = async () => {
      const url = `https://lunaloo.net/?join=${this.hh.code}`;
      const note = this.el.querySelector<HTMLElement>('#careShareMsg')!;
      try {
        if (navigator.share) await navigator.share({ title: 'Lunaloo', text: `Come look after ${this.dogName} with me`, url });
        else {
          await navigator.clipboard.writeText(url);
          note.textContent = 'link copied';
        }
      } catch {
        note.textContent = url;
      }
    };
    const worn = unlockedCollar(c.state.bestStreak);
    const next = nextCollar(c.state.bestStreak);
    q('#careCollars').textContent =
      COLLARS.filter((k) => k.tier > 0)
        .map((k) => `${k.tier <= worn.tier ? '✓' : '·'} ${k.name} (${k.streak}d)`)
        .join('   ') + (next ? `\n${next.streak - c.state.bestStreak} more streak day${next.streak - c.state.bestStreak === 1 ? '' : 's'} to the ${next.name}` : '\nshe has them all');
    this.el.querySelector<HTMLInputElement>('#careName')!.value = this.hh.name;
  }
}
